<template>
  <div class="supplier-api-documentation mt-3">
    <header class="supplier-api-doc__header">
      <div class="supplier-api-doc__title">
        <span class="supplier-api-doc__icon">
          <feather-icon
            icon="TerminalIcon"
            size="18"
          />
        </span>
        <div>
          <h5>Documentation Technique API</h5>
          <p>Guide d'intégration fournisseur pour créer et suivre des colis.</p>
        </div>
      </div>
      <div class="supplier-api-doc__actions">
        <b-button
          variant="flat-light"
          size="sm"
          class="supplier-api-doc__copy-all"
          @click="copyText(documentationText, 'Documentation API copiée')"
        >
          <feather-icon
            icon="CopyIcon"
            size="15"
            class="mr-50"
          />
          Copier la doc
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="supplier-api-doc__export"
          :disabled="!token || isExportingPdf"
          @click="exportPdf"
        >
          <b-spinner
            v-if="isExportingPdf"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="15"
            class="mr-50"
          />
          {{ isExportingPdf ? 'Préparation...' : 'Exporter en PDF' }}
        </b-button>
      </div>
    </header>

    <div class="supplier-api-doc__base">
      <div>
        <span>Base URL</span>
        <code>{{ apiBaseUrl }}</code>
      </div>
      <button
        type="button"
        title="Copier la Base URL"
        aria-label="Copier la Base URL"
        @click="copyText(apiBaseUrl, 'Base URL copiée')"
      >
        <feather-icon
          icon="CopyIcon"
          size="15"
        />
      </button>
    </div>

    <section class="supplier-api-doc__auth">
      <h6>
        <feather-icon
          icon="KeyIcon"
          size="14"
        />
        Authentification
      </h6>
      <p>Ajoutez votre token API fournisseur dans le header de chaque requête protégée.</p>
      <pre>{{ authenticationHeaders }}</pre>
    </section>

    <section
      v-for="endpoint in endpointSections"
      :key="endpoint.step"
      class="supplier-api-doc__endpoint"
    >
      <div class="supplier-api-doc__endpoint-heading">
        <span>{{ endpoint.step }}</span>
        <div>
          <h6>{{ endpoint.title }}</h6>
          <p>{{ endpoint.description }}</p>
        </div>
      </div>

      <div class="supplier-api-doc__url">
        <span :class="`method-${endpoint.method.toLowerCase()}`">{{ endpoint.method }}</span>
        <code>{{ endpoint.url }}</code>
        <button
          type="button"
          title="Copier l'URL"
          :aria-label="`Copier l'URL ${endpoint.title}`"
          @click="copyText(endpoint.url, 'URL copiée')"
        >
          <feather-icon
            icon="CopyIcon"
            size="14"
          />
        </button>
      </div>

      <div class="supplier-api-doc__request">
        <div class="supplier-api-doc__subheading">
          <span>
            <feather-icon
              icon="UploadCloudIcon"
              size="14"
            />
            Requête
          </span>
          <button
            type="button"
            title="Copier la requête"
            :aria-label="`Copier la requête ${endpoint.title}`"
            @click="copyText(endpoint.fullRequest, 'Requête copiée')"
          >
            <feather-icon
              icon="CopyIcon"
              size="14"
            />
          </button>
        </div>
        <pre>{{ endpoint.request }}</pre>
        <div
          v-if="endpoint.requestBody"
          class="supplier-api-doc__code-wrapper"
        >
          <button
            type="button"
            title="Copier le JSON"
            aria-label="Copier le corps JSON"
            @click="copyText(endpoint.requestBody, 'Corps JSON copié')"
          >
            <feather-icon
              icon="CopyIcon"
              size="14"
            />
          </button>
          <pre>{{ endpoint.requestBody }}</pre>
        </div>
        <div
          v-if="endpoint.fields"
          class="supplier-api-doc__fields"
        >
          <div
            v-for="fieldGroup in endpoint.fields"
            :key="fieldGroup.title"
          >
            <strong>{{ fieldGroup.title }}</strong>
            <span>{{ fieldGroup.value }}</span>
          </div>
        </div>
      </div>

      <div class="supplier-api-doc__response">
        <div class="supplier-api-doc__subheading">
          <span>
            <feather-icon
              icon="DownloadCloudIcon"
              size="14"
            />
            Réponse
          </span>
          <em>{{ endpoint.responseStatus }}</em>
        </div>
        <pre>{{ endpoint.response }}</pre>
        <p
          v-for="note in endpoint.notes"
          :key="note"
        >
          {{ note }}
        </p>
      </div>
    </section>

    <section class="supplier-api-doc__endpoint">
      <div class="supplier-api-doc__endpoint-heading">
        <span>06</span>
        <div>
          <h6>Correspondance des statuts</h6>
          <p>Utilisez le code numérique retourné dans status pour synchroniser l'état du colis.</p>
        </div>
      </div>
      <div class="supplier-api-doc__statuses">
        <div
          v-for="status in orderStatusMappings"
          :key="status.code"
        >
          <strong>{{ status.code }}</strong>
          <span>{{ status.label }}</span>
        </div>
      </div>
      <p class="supplier-api-doc__status-note">
        <code>status</code> est la valeur stable à enregistrer. Utilisez
        <code>status_display</code> comme libellé destiné à l'affichage.
      </p>
    </section>

    <section class="supplier-api-doc__errors">
      <h6>Erreurs courantes</h6>
      <div>
        <span><strong>401</strong> Token absent ou invalide</span>
        <span><strong>403</strong> Compte non autorisé</span>
        <span><strong>400</strong> Champ manquant ou format invalide</span>
      </div>
    </section>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import axios from 'axios'
import JsPDF from 'jspdf'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const governorateFallback = [
  { id: 1, name: 'Ariana' }, { id: 2, name: 'Béja' },
  { id: 3, name: 'Ben Arous' }, { id: 4, name: 'Bizerte' },
  { id: 5, name: 'Gabès' }, { id: 6, name: 'Gafsa' },
  { id: 7, name: 'Jendouba' }, { id: 8, name: 'Kairouan' },
  { id: 9, name: 'Kasserine' }, { id: 10, name: 'Kébili' },
  { id: 11, name: 'Kef' }, { id: 12, name: 'Mahdia' },
  { id: 13, name: 'Manouba' }, { id: 14, name: 'Médenine' },
  { id: 15, name: 'Monastir' }, { id: 16, name: 'Nabeul' },
  { id: 17, name: 'Sfax' }, { id: 18, name: 'Sidi Bouzid' },
  { id: 19, name: 'Siliana' }, { id: 20, name: 'Sousse' },
  { id: 21, name: 'Tataouine' }, { id: 22, name: 'Tozeur' },
  { id: 23, name: 'Tunis' }, { id: 24, name: 'Zaghouan' },
]

const orderStatusMappings = [
  { code: 0, label: 'En attente' }, { code: 1, label: 'En pickup' },
  { code: 2, label: 'Colis au dépôt' }, { code: 3, label: 'En cours' },
  { code: 4, label: 'Livré' }, { code: 5, label: 'Retour au dépôt' },
  { code: 6, label: 'Livré payé' }, { code: 7, label: 'Retour au fournisseur' },
  { code: 8, label: 'En transfert' }, { code: 9, label: 'Retour payé' },
  { code: 10, label: 'Retour inter-agence' }, { code: 11, label: 'Retour inter-agence reçu' },
  { code: 12, label: 'Colis annulé par admin' }, { code: 13, label: 'Colis perdu' },
  { code: 14, label: 'À vérifier' }, { code: 15, label: 'En attente de collecte' },
  { code: 16, label: 'À rembourser' }, { code: 17, label: 'Remboursé' },
  { code: 18, label: 'Planification' }, { code: 19, label: 'Livré temporaire' },
  { code: 20, label: 'Retour temporaire' }, { code: 21, label: 'Enlevé' },
  { code: 22, label: 'Retour définitif' }, { code: 23, label: 'Relance' },
  { code: 24, label: 'Anomalie de livraison' }, { code: 25, label: 'Échange' },
  { code: 26, label: 'Échange reçu' }, { code: 27, label: 'Anomalie de pickup' },
  { code: 28, label: 'Retour payé reçu' },
]

export default {
  name: 'SupplierApiDocumentation',
  components: { BButton, BSpinner },
  props: {
    token: { type: String, default: '' },
  },
  data() {
    return {
      governorates: [],
      isExportingPdf: false,
      orderStatusMappings,
    }
  },
  computed: {
    apiBaseUrl() {
      const configuredUrl = process.env.VUE_APP_baseUrl || window.location.origin
      return configuredUrl.replace(/\/+$/, '')
    },
    authenticationHeaders() {
      return 'Authorization: Token <SUPPLIER_API_TOKEN>\nContent-Type: application/json'
    },
    createOrderPayload() {
      return JSON.stringify({
        name: 'Mohamed Ben Ali',
        phone1: '22334455',
        address: '15 Rue de la Republique, Tunis',
        governorate: 23,
        delegation: 111,
        locality: 1,
        price_ttc: '145.500',
        note: 'Livrer apres 17h',
        products: [{ product: 'Vetements', quantity: 1 }],
        total_boxes: 1,
        can_open_package: true,
        is_an_exchange: false,
        source_creation: 'api_partner',
      }, null, 2)
    },
    governoratesResponse() {
      return JSON.stringify(this.governorates.length ? this.governorates : governorateFallback, null, 2)
    },
    endpointSections() {
      const headers = this.authenticationHeaders
      const endpoints = [
        {
          step: '01',
          title: 'Créer un colis',
          description: 'Crée une nouvelle commande de livraison pour le fournisseur authentifié.',
          method: 'POST',
          url: `${this.apiBaseUrl}/api/orders/create/`,
          request: headers,
          requestBody: this.createOrderPayload,
          responseStatus: '201 Created',
          response: JSON.stringify({
            id: '26012345', status: 0, external_id: null, phone1: '22334455',
          }, null, 2),
          fields: [
            { title: 'Champs obligatoires', value: 'name, phone1, address, governorate, price_ttc' },
            { title: 'Champs recommandés', value: 'delegation, locality, products' },
            { title: 'Champs retournés', value: 'id, status, external_id, phone1' },
          ],
          notes: [],
        },
        {
          step: '02',
          title: 'Récupérer les gouvernorats',
          description: 'Retourne les gouvernorats et les IDs à envoyer lors de la création d’un colis.',
          method: 'GET',
          url: `${this.apiBaseUrl}/api/governorates/`,
          request: 'Authorization: Token <SUPPLIER_API_TOKEN>',
          responseStatus: '200 OK',
          response: this.governoratesResponse,
          notes: [],
        },
        {
          step: '03',
          title: 'Récupérer les délégations',
          description: "Filtre les délégations à partir de l'ID d'un gouvernorat.",
          method: 'GET',
          url: `${this.apiBaseUrl}/api/delegations/?governorate=23`,
          request: 'Authorization: Token <SUPPLIER_API_TOKEN>\n\nParamètre: governorate=23',
          responseStatus: '200 OK',
          response: JSON.stringify([{
            id: 111,
            name: 'Cité El Khadra',
            is_active: true,
            governorate: { id: 23, name: 'Tunis' },
          }], null, 2),
          notes: [],
        },
        {
          step: '04',
          title: 'Récupérer la liste des colis',
          description: 'Retourne les colis du fournisseur authentifié avec pagination et statut actuel.',
          method: 'GET',
          url: `${this.apiBaseUrl}/api/orders/list/?page=1&page_size=20`,
          request: 'Authorization: Token <SUPPLIER_API_TOKEN>\n\nParamètres: page=1, page_size=20\nFiltres: status=3 ou status=3,4, search=26012345, ordering=-created_at',
          responseStatus: '200 OK',
          response: JSON.stringify({
            count: 42,
            next: `${this.apiBaseUrl}/api/orders/list/?page=2&page_size=20`,
            previous: null,
            results: [{
              id: '26012345',
              external_id: 'PARTNER-ORDER-10001',
              name: 'Mohamed Ben Ali',
              phone1: '22334455',
              status: 5,
              status_display: 'Retour au dépôt',
              driver_name: 'Ahmed Trabelsi',
              driver_phone: '20111222',
              last_return_reason: {
                return_reason: 'Client injoignable', created_at: '2026-09-05 11:30:00',
              },
            }],
          }, null, 2),
          notes: [
            'driver_name et driver_phone valent null avant l’affectation d’un livreur.',
            'Utilisez id pour appeler le détail et synchroniser le statut dans votre système.',
          ],
        },
        {
          step: '05',
          title: 'Récupérer un colis par ID',
          description: 'Retourne le détail, le statut, le livreur et les motifs de retour du colis.',
          method: 'GET',
          url: `${this.apiBaseUrl}/api/orders/detail/26012345/`,
          request: 'Authorization: Token <SUPPLIER_API_TOKEN>\n\nRemplacez 26012345 par l’ID Rapidex du colis.',
          responseStatus: '200 OK',
          response: JSON.stringify({
            id: '26012345',
            external_id: 'PARTNER-ORDER-10001',
            phone1: '22334455',
            status: 5,
            status_display: 'Retour au dépôt',
            driver_name: 'Ahmed Trabelsi',
            driver_phone: '20111222',
            last_return_reason: {
              return_reason: 'Client injoignable', created_at: '2026-09-05 11:30:00',
            },
            return_reason_histories: [
              {
                return_reason: 'Client injoignable',
                created_at: '2026-09-05 11:30:00',
                scheduled_date: '2026-09-06 14:00:00',
                additional_note: 'Deuxième numéro sans réponse',
                quick_options: ['telephone_ferme'],
              },
              { return_reason: 'Client absent', created_at: '2026-09-04 16:10:00' },
            ],
          }, null, 2),
          notes: [
            'Lisez last_return_reason pour obtenir immédiatement le dernier motif.',
            'Lisez return_reason_histories pour l’historique complet, du plus récent au plus ancien.',
          ],
        },
      ]
      return endpoints.map(endpoint => ({
        ...endpoint,
        requestBody: endpoint.requestBody || '',
        fields: endpoint.fields || null,
        fullRequest: `${endpoint.method} ${endpoint.url}\n${endpoint.request}${endpoint.requestBody ? `\n\n${endpoint.requestBody}` : ''}`,
      }))
    },
    documentationText() {
      const endpointText = this.endpointSections.map(endpoint => [
        `${endpoint.step}. ${endpoint.title.toUpperCase()}`,
        `${endpoint.method} ${endpoint.url}`,
        'Requête:',
        endpoint.request,
        endpoint.requestBody,
        `Réponse ${endpoint.responseStatus}:`,
        endpoint.response,
        ...endpoint.notes,
      ].filter(Boolean).join('\n')).join('\n\n')
      const statuses = this.orderStatusMappings.map(status => `${status.code}: ${status.label}`).join('\n')
      return `Documentation API ${this.$branding.company.name}\n\nBase URL: ${this.apiBaseUrl}\n\nAUTHENTIFICATION\n${this.authenticationHeaders}\n\n${endpointText}\n\n06. CORRESPONDANCE DES STATUTS\n${statuses}`
    },
  },
  created() {
    this.fetchGovernorates()
  },
  methods: {
    notify(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification', icon: 'InfoIcon', text, variant,
        },
      }, { position: 'top-center' })
    },
    async copyText(text, successMessage) {
      try {
        await navigator.clipboard.writeText(text)
        this.notify('success', successMessage)
      } catch (error) {
        this.notify('danger', 'Impossible de copier les données')
      }
    },
    async fetchGovernorates() {
      try {
        const { data } = await axios.get('/api/governorates/')
        const values = Array.isArray(data) ? data : data.results
        this.governorates = Array.isArray(values)
          ? values.map(({ id, name }) => ({ id, name }))
          : []
      } catch (error) {
        this.governorates = []
      }
    },
    async loadLogoData() {
      const response = await fetch(this.$branding.logo.main)
      if (!response.ok) throw new Error('Logo indisponible')
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => resolve(reader.result), { once: true })
        reader.addEventListener('error', reject, { once: true })
        reader.readAsDataURL(blob)
      })
    },
    brandRgb() {
      const value = this.$branding.theme.primary || '#C62828'
      const match = /^#([0-9a-f]{6})$/i.exec(value)
      if (!match) return [198, 40, 40]
      return [
        parseInt(match[1].slice(0, 2), 16),
        parseInt(match[1].slice(2, 4), 16),
        parseInt(match[1].slice(4, 6), 16),
      ]
    },
    async exportPdf() {
      if (!this.token) {
        this.notify('danger', 'Le token API est requis pour générer le PDF')
        return
      }
      this.isExportingPdf = true
      try {
        const pdf = new JsPDF('p', 'mm', 'a4')
        const pageWidth = 210
        const margin = 14
        const contentWidth = 182
        const top = 29
        const bottom = 280
        const primary = this.brandRgb()
        const navy = [23, 32, 51]
        const slate = [71, 85, 105]
        const border = [220, 228, 237]
        const surface = [248, 250, 252]
        const blue = [37, 99, 235]
        const green = [22, 121, 75]
        const logo = await this.loadLogoData().catch(() => null)
        let y = top

        const setFont = (family, style, size, color) => {
          pdf.setFont(family)
          pdf.setFontStyle(style)
          pdf.setFontSize(size)
          pdf.setTextColor(...color)
        }
        const header = () => {
          pdf.setDrawColor(...primary)
          pdf.setLineWidth(0.8)
          pdf.line(margin, 11, pageWidth - margin, 11)
          if (logo) pdf.addImage(logo, 'PNG', margin, 14, 18, 11)
          setFont('helvetica', 'bold', 9, navy)
          pdf.text(this.$branding.company.name, logo ? 36 : margin, 18)
          setFont('helvetica', 'normal', 7, slate)
          pdf.text('Documentation technique API', logo ? 36 : margin, 22)
          y = top
        }
        const newPage = () => {
          pdf.addPage()
          header()
        }
        const ensure = height => {
          if (y + height > bottom) newPage()
        }
        const paragraph = (text, options = {}) => {
          if (!text) return
          const lineHeight = options.lineHeight || 4.2
          setFont('helvetica', options.bold ? 'bold' : 'normal', options.size || 8, options.color || slate)
          const lines = pdf.splitTextToSize(text, options.width || contentWidth)
          ensure((lines.length * lineHeight) + 2)
          lines.forEach(line => {
            pdf.text(line, options.x || margin, y)
            y += lineHeight
          })
          y += 2
        }
        const sectionLabel = (label, color = primary) => {
          ensure(10)
          pdf.setFillColor(...color)
          pdf.roundedRect(margin, y - 3, 3, 7, 1, 1, 'F')
          setFont('helvetica', 'bold', 9, navy)
          pdf.text(label, margin + 7, y + 1.5)
          y += 10
        }
        const codeLines = text => text.split('\n').reduce((lines, line) => (
          lines.concat(pdf.splitTextToSize(line || ' ', contentWidth - 12))
        ), [])
        const codeBlock = (label, text, accent, keepTogether = false) => {
          if (!text) return
          setFont('courier', 'normal', 7.4, navy)
          const lines = codeLines(text.split('<SUPPLIER_API_TOKEN>').join(this.token))
          const lineHeight = 3.65
          const labelHeight = 7
          const padding = 4.5
          const fullHeight = labelHeight + (lines.length * lineHeight) + (padding * 2)
          const maximumHeight = bottom - top
          if (keepTogether && fullHeight <= maximumHeight) ensure(fullHeight + 3)
          let index = 0
          let continued = false
          while (index < lines.length) {
            if (y + labelHeight + (lineHeight * 3) + (padding * 2) > bottom) newPage()
            const available = bottom - y - labelHeight - (padding * 2)
            const count = keepTogether && fullHeight <= maximumHeight
              ? lines.length - index
              : Math.min(lines.length - index, Math.max(1, Math.floor(available / lineHeight)))
            const height = labelHeight + (count * lineHeight) + (padding * 2)
            setFont('helvetica', 'bold', 8, accent)
            pdf.text(continued ? `${label} (SUITE)` : label, margin, y + 1)
            y += labelHeight
            pdf.setFillColor(...surface)
            pdf.setDrawColor(...border)
            pdf.roundedRect(margin, y, contentWidth, height - labelHeight, 2, 2, 'FD')
            y += padding + 1
            setFont('courier', 'normal', 7.4, navy)
            const lastIndex = index + count
            for (let lineIndex = index; lineIndex < lastIndex; lineIndex += 1) {
              pdf.text(lines[lineIndex], margin + 5, y)
              y += lineHeight
            }
            y += padding
            index += count
            continued = true
            if (index < lines.length) newPage()
          }
          y += 4
        }
        const endpointHeader = endpoint => {
          const descriptions = pdf.splitTextToSize(endpoint.description, contentWidth - 18)
          ensure(27 + (descriptions.length * 4))
          pdf.setFillColor(255, 240, 240)
          pdf.roundedRect(margin, y - 3, 10, 10, 2, 2, 'F')
          setFont('helvetica', 'bold', 8, primary)
          pdf.text(endpoint.step, margin + 2.3, y + 3.4)
          setFont('helvetica', 'bold', 11, navy)
          pdf.text(endpoint.title, margin + 15, y + 1)
          y += 7
          setFont('helvetica', 'normal', 8, slate)
          descriptions.forEach(line => {
            pdf.text(line, margin + 15, y)
            y += 4
          })
          y += 3
          const urls = pdf.splitTextToSize(endpoint.url, contentWidth - 31)
          const height = Math.max(12, 6 + (urls.length * 3.8))
          pdf.setFillColor(...surface)
          pdf.setDrawColor(...border)
          pdf.roundedRect(margin, y, contentWidth, height, 2, 2, 'FD')
          pdf.setFillColor(...(endpoint.method === 'POST' ? primary : green))
          pdf.roundedRect(margin + 4, y + 2.5, 19, 7, 1.5, 1.5, 'F')
          setFont('helvetica', 'bold', 7, [255, 255, 255])
          pdf.text(endpoint.method, margin + 7, y + 7.2)
          setFont('courier', 'bold', 7.3, navy)
          urls.forEach((line, index) => pdf.text(line, margin + 27, y + 6.5 + (index * 3.8)))
          y += height + 5
        }

        pdf.setDrawColor(...primary)
        pdf.setLineWidth(1.2)
        pdf.line(margin, 13, pageWidth - margin, 13)
        if (logo) pdf.addImage(logo, 'PNG', margin, 20, 25, 15.3)
        setFont('helvetica', 'bold', 12, navy)
        pdf.text(this.$branding.company.name, logo ? 44 : margin, 25)
        setFont('helvetica', 'normal', 8, slate)
        pdf.text("Guide d'intégration fournisseur", logo ? 44 : margin, 30)
        setFont('helvetica', 'bold', 9, primary)
        pdf.text('DOCUMENTATION TECHNIQUE', margin, 53)
        setFont('helvetica', 'bold', 24, navy)
        pdf.text('API de gestion des colis', margin, 65)
        setFont('helvetica', 'normal', 9, slate)
        pdf.text(`Généré le ${new Intl.DateTimeFormat('fr-TN', { dateStyle: 'long' }).format(new Date())}`, margin, 74)

        const credential = (x, width, label, value) => {
          pdf.setFillColor(...surface)
          pdf.setDrawColor(...border)
          pdf.roundedRect(x, 86, width, 31, 2, 2, 'FD')
          setFont('helvetica', 'bold', 7.5, slate)
          pdf.text(label, x + 5, 93)
          setFont('courier', 'bold', 8, navy)
          pdf.splitTextToSize(value, width - 10).slice(0, 4).forEach((line, index) => {
            pdf.text(line, x + 5, 101 + (index * 4))
          })
        }
        credential(margin, 76, 'BASE URL', this.apiBaseUrl)
        credential(margin + 82, 100, 'TOKEN API FOURNISSEUR', this.token)
        pdf.setFillColor(255, 245, 245)
        pdf.roundedRect(margin, 123, contentWidth, 15, 2, 2, 'F')
        setFont('helvetica', 'bold', 7.5, [127, 29, 29])
        pdf.splitTextToSize(
          'Document confidentiel. Le token donne accès à vos données fournisseur et ne doit pas être partagé publiquement.',
          contentWidth - 10,
        ).forEach((line, index) => pdf.text(line, margin + 5, 130 + (index * 3.8)))
        setFont('helvetica', 'bold', 8, slate)
        pdf.text('CONTENU DU GUIDE', margin, 151)
        const summary = this.endpointSections.map(endpoint => endpoint.title)
          .concat('Correspondance des statuts')
        summary.forEach((item, index) => {
          const x = margin + ((index % 2) * 91)
          const itemY = 161 + (Math.floor(index / 2) * 14)
          pdf.setFillColor(255, 240, 240)
          pdf.roundedRect(x, itemY - 5, 10, 9, 1.5, 1.5, 'F')
          setFont('helvetica', 'bold', 7, primary)
          pdf.text(String(index + 1).padStart(2, '0'), x + 2.1, itemY + 0.8)
          setFont('helvetica', 'bold', 8, navy)
          pdf.text(item, x + 14, itemY + 0.8)
        })

        newPage()
        sectionLabel('AUTHENTIFICATION')
        paragraph('Ajoutez votre token API fournisseur dans le header de chaque requête protégée.')
        codeBlock('EN-TÊTES À ENVOYER', this.authenticationHeaders, blue, true)
        this.endpointSections.forEach(endpoint => {
          ensure(30)
          endpointHeader(endpoint)
          codeBlock('REQUÊTE', `${endpoint.request}${endpoint.requestBody ? `\n\n${endpoint.requestBody}` : ''}`, blue, true)
          codeBlock(`RÉPONSE · ${endpoint.responseStatus}`, endpoint.response, green)
          endpoint.notes.forEach(note => paragraph(note, { size: 7.8 }))
          y += 5
        })
        sectionLabel('CODES DE STATUT')
        for (let index = 0; index < this.orderStatusMappings.length; index += 2) {
          ensure(10)
          const statuses = this.orderStatusMappings.slice(index, index + 2)
          for (let column = 0; column < statuses.length; column += 1) {
            const status = statuses[column]
            const width = 88
            const x = margin + (column * 94)
            pdf.setFillColor(...surface)
            pdf.setDrawColor(...border)
            pdf.roundedRect(x, y, width, 8, 1.5, 1.5, 'FD')
            setFont('helvetica', 'bold', 7.2, primary)
            pdf.text(String(status.code), x + 3, y + 5.2)
            setFont('helvetica', 'bold', 7.2, navy)
            pdf.text(status.label, x + 13, y + 5.2)
          }
          y += 10
        }

        const pages = pdf.internal.getNumberOfPages()
        for (let page = 1; page <= pages; page += 1) {
          pdf.setPage(page)
          pdf.setDrawColor(...border)
          pdf.line(margin, 286, pageWidth - margin, 286)
          setFont('helvetica', 'normal', 7, slate)
          pdf.text(`${this.$branding.company.name} · Document confidentiel`, margin, 291)
          pdf.text(`Page ${page} / ${pages}`, pageWidth - 35, 291)
        }
        const safeBrand = this.$branding.company.name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/gi, '-')
          .replace(/^-|-$/g, '')
          .toLowerCase()
        pdf.save(`documentation-api-${safeBrand}-${new Date().toISOString().slice(0, 10)}.pdf`)
        this.notify('success', 'Documentation PDF téléchargée')
      } catch (error) {
        this.notify('danger', 'Impossible de générer la documentation PDF')
      } finally {
        this.isExportingPdf = false
      }
    },
  },
}
</script>

<style lang="scss">
.supplier-api-documentation {
  --doc-primary: var(--brand-primary, #c62828);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
  color: #172033;
  font-weight: 600;

  button { cursor: pointer; }
  pre {
    margin: 0;
    border: 1px solid #dce4ed;
    border-radius: 7px;
    padding: 0.9rem 1rem;
    overflow: auto;
    background: #f8fafc;
    color: #172033;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
  }
}
.supplier-api-doc__header,
.supplier-api-doc__title,
.supplier-api-doc__actions,
.supplier-api-doc__base,
.supplier-api-doc__url,
.supplier-api-doc__subheading { display: flex; align-items: center; }
.supplier-api-doc__header { justify-content: space-between; gap: 1rem; padding-bottom: 1rem; border-bottom: 1px solid #e8edf3; }
.supplier-api-doc__title { align-items: flex-start; gap: 0.8rem; }
.supplier-api-doc__title h5 { margin: 0 0 0.2rem; font-weight: 800; }
.supplier-api-doc__title p { margin: 0; color: #64748b; font-size: 0.85rem; }
.supplier-api-doc__icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; flex: 0 0 38px; border: 1px solid #ffd6d6; border-radius: 8px; background: #fff0f0; color: var(--doc-primary); }
.supplier-api-doc__actions { gap: 0.6rem; }
.supplier-api-doc__copy-all,
.supplier-api-doc__export { border-radius: 7px !important; font-weight: 700; white-space: nowrap; }
.supplier-api-doc__export { background: var(--doc-primary) !important; border-color: var(--doc-primary) !important; }
.supplier-api-doc__base { justify-content: space-between; gap: 1rem; margin: 1rem 0; border: 1px solid #dce4ed; border-radius: 7px; padding: 0.85rem 1rem; background: #f8fafc; }
.supplier-api-doc__base span { display: block; margin-bottom: 0.25rem; color: #94a3b8; font-size: 0.68rem; font-weight: 800; text-transform: uppercase; }
.supplier-api-doc__base code { color: #172033; background: transparent; font-weight: 700; word-break: break-all; }
.supplier-api-doc__base button,
.supplier-api-doc__url button,
.supplier-api-doc__subheading button,
.supplier-api-doc__code-wrapper > button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #dce4ed; border-radius: 6px; background: #fff; color: #64748b; }
.supplier-api-doc__auth { margin-top: 1rem; }
.supplier-api-doc__auth h6 { display: flex; align-items: center; gap: 0.4rem; color: var(--doc-primary); font-size: 0.8rem; font-weight: 800; text-transform: uppercase; }
.supplier-api-doc__auth p { color: #64748b; font-size: 0.86rem; }
.supplier-api-doc__endpoint { margin-top: 1.5rem; border-top: 1px solid #e8edf3; padding-top: 1.5rem; }
.supplier-api-doc__endpoint-heading { display: flex; align-items: flex-start; gap: 0.85rem; margin-bottom: 0.9rem; }
.supplier-api-doc__endpoint-heading > span { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex: 0 0 32px; border: 1px solid #ffd6d6; border-radius: 50%; background: #fff0f0; color: var(--doc-primary); font-size: 0.72rem; font-weight: 800; }
.supplier-api-doc__endpoint-heading h6 { margin: 0 0 0.2rem; font-size: 1rem; font-weight: 800; }
.supplier-api-doc__endpoint-heading p { margin: 0; color: #64748b; font-size: 0.84rem; }
.supplier-api-doc__url { gap: 0.7rem; border: 1px solid #dce4ed; border-radius: 7px; padding: 0.65rem 0.8rem; background: #f8fafc; }
.supplier-api-doc__url > span { min-width: 48px; border-radius: 5px; padding: 0.28rem 0.45rem; color: #fff; font-size: 0.68rem; font-weight: 800; text-align: center; }
.supplier-api-doc__url .method-post { background: var(--doc-primary); }
.supplier-api-doc__url .method-get { background: #16794b; }
.supplier-api-doc__url code { min-width: 0; flex: 1; overflow: auto; color: #172033; background: transparent; font-weight: 700; white-space: nowrap; }
.supplier-api-doc__request { margin-top: 1rem; border: 1px solid #cbd5e1; border-left: 4px solid #2563eb; border-radius: 8px; padding: 0.9rem; background: #f8fbff; }
.supplier-api-doc__response { margin-top: 1rem; }
.supplier-api-doc__response > p { margin: 0.55rem 0 0; color: #64748b; font-size: 0.78rem; }
.supplier-api-doc__subheading { justify-content: space-between; margin-bottom: 0.55rem; color: #475569; font-size: 0.76rem; font-weight: 800; text-transform: uppercase; }
.supplier-api-doc__subheading > span { display: flex; align-items: center; gap: 0.4rem; }
.supplier-api-doc__subheading em { border-radius: 5px; padding: 0.2rem 0.45rem; background: #eaf8f1; color: #16794b; font-size: 0.66rem; font-style: normal; text-transform: none; }
.supplier-api-doc__request > pre + *,
.supplier-api-doc__code-wrapper { margin-top: 0.75rem; }
.supplier-api-doc__code-wrapper { position: relative; }
.supplier-api-doc__code-wrapper > button { position: absolute; top: 0.55rem; right: 0.55rem; z-index: 2; }
.supplier-api-doc__code-wrapper pre { padding-right: 3.5rem; }
.supplier-api-doc__fields { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.7rem; margin-top: 0.8rem; }
.supplier-api-doc__fields > div { border: 1px solid #fee2e2; border-radius: 7px; padding: 0.75rem; background: #fffafa; color: #64748b; font-size: 0.76rem; }
.supplier-api-doc__fields strong { display: block; margin-bottom: 0.2rem; color: #172033; }
.supplier-api-doc__statuses { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.55rem; }
.supplier-api-doc__statuses > div { display: flex; min-height: 40px; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 7px; }
.supplier-api-doc__statuses strong { display: inline-flex; align-items: center; justify-content: center; width: 40px; flex: 0 0 40px; background: #f8fafc; color: var(--doc-primary); }
.supplier-api-doc__statuses span { display: flex; align-items: center; padding: 0.55rem 0.65rem; color: #334155; font-size: 0.76rem; }
.supplier-api-doc__status-note { margin: 0.8rem 0 0; border: 1px solid #dce4ed; border-radius: 7px; padding: 0.75rem; background: #f8fafc; color: #64748b; font-size: 0.78rem; }
.supplier-api-doc__errors { margin-top: 1.5rem; border-top: 1px solid #e8edf3; padding-top: 1.2rem; }
.supplier-api-doc__errors h6 { color: var(--doc-primary); font-size: 0.78rem; font-weight: 800; text-transform: uppercase; }
.supplier-api-doc__errors > div { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.7rem; }
.supplier-api-doc__errors span { border: 1px solid #fed7aa; border-radius: 7px; padding: 0.75rem; background: #fff7ed; color: #64748b; font-size: 0.76rem; }
.supplier-api-doc__errors strong { display: block; color: #c2410c; font-size: 0.9rem; }
@media (max-width: 991.98px) {
  .supplier-api-doc__header { align-items: stretch; flex-direction: column; }
  .supplier-api-doc__fields,
  .supplier-api-doc__statuses,
  .supplier-api-doc__errors > div { grid-template-columns: 1fr; }
}
@media (max-width: 575.98px) {
  .supplier-api-documentation { padding: 1rem; }
  .supplier-api-doc__actions { align-items: stretch; flex-direction: column; }
  .supplier-api-doc__actions .btn { width: 100%; }
  .supplier-api-doc__url { align-items: flex-start; flex-wrap: wrap; }
  .supplier-api-doc__url code { width: calc(100% - 80px); white-space: normal; word-break: break-all; }
}
</style>
